﻿import { MouseEventHandler, useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';

import { ActivateAccountReq } from 'services/auth.service';
import { useAppDispatch } from 'store/store-hooks';
import { Path, useAuthNavigator } from 'components/_hooks/use-router';
import { activateAcc, resendSignUpOtpCode } from '../auth.slice';

import { Button } from 'components/UI/button/button';
import { InputGroup } from 'components/UI/input/input-group';
import { InputBase } from 'components/UI/input/elements/input-base';
import styles from '../_auth.module.scss';
import { LocalStore } from 'constants/localstore';

const timer = 60;

export const AuthActivateAcc = () => {
  const [isPending, setIsPending] = useState<boolean>(false);
  const [iterator, setIterator] = useState<number>(timer);
  const dispatch = useAppDispatch();
  const { navigateTo } = useAuthNavigator();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ActivateAccountReq>();

  const onSubmit: SubmitHandler<ActivateAccountReq> = async (data) => {
    const urlParams = new URLSearchParams(window.location.search);
    data.email = urlParams.get('email') ?? '';
    data.userName = urlParams.get('username') ?? '';
    try {
      setIsPending(true);
      const res = await dispatch(activateAcc(data)).unwrap();
      if (res.executionResult) {
        toast.info('Your account was activated');
        localStorage.removeItem(LocalStore.startTour);
        navigateTo(Path.pulseIntro);
      }
    } finally {
      setIsPending(false);
    }
  };

  const resendCode: MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    const urlParams = new URLSearchParams(window.location.search);
    try {
      setIsPending(true);
      const res = await dispatch(resendSignUpOtpCode({ email: urlParams.get('email') ?? '' })).unwrap();
      if (res.executionResult) toast.info('OTP sent again.');
      setIterator(timer);
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    if (iterator == 0) {
      return;
    } else {
      const t = setTimeout(async () => {
        setIterator(iterator - 1);
        clearInterval(t);
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [iterator]);

  return (
    <div className={styles.Login}>
      <h3 className={styles.title}>Activate account</h3>
      <p className={styles.desc}>We’ve sent a One-Time Password (OTP) to your registered email.</p>
      <p className={styles.desc}>Please enter the code below to continue.</p>
      <form className={styles.form}>
        <InputGroup header="OTP Code" error={errors.otpCode?.message}>
          <InputBase
            placeholder="Enter OTP Code"
            {...register('otpCode', {
              required: 'The OTP Code is required',
              validate: (value) => value.length === 8 || 'Must be 8 characters',
            })}
            alert={!!errors.otpCode?.message}
          />
        </InputGroup>
        <footer>
          <Button disabled={isPending || iterator !== 0} style={{ marginRight: '10px' }} onClick={resendCode}>
            Resend OTP
          </Button>
          <Button
            disabled={isPending || !!Object.keys(errors).length}
            style={{ marginLeft: '10px' }}
            onClick={handleSubmit(onSubmit)}
          >
            Activate
          </Button>
          <a hidden={iterator === 0}>
            You can request the OTP again in <strong>{iterator}</strong> seconds.
          </a>
        </footer>
      </form>
    </div>
  );
};
